import type { CardSide, MapPoint } from "./landing-hero-orders";
import { US_MAP_WIDTH } from "./landing-network-map";

export interface PlacementSize {
  width: number;
  height: number;
}

export interface CardRequest {
  key: string;
  point: MapPoint;
  preferred: CardSide;
  height: number;
}

export interface CardBox {
  left: number;
  top: number;
}

interface Rect extends CardBox {
  width: number;
  height: number;
}

interface Obstacle extends Rect {
  weight: number;
}

export const CARD_WIDTH_PX = 256;
const GAPS_PX = [18, 56];
const CORNER_INSET_PX = 28;
const PIN_CLEARANCE_PX = 22;
const ROUTE_CLEARANCE_PX = 10;
const EDGE_SLACK_PX = -8;
const OUT_OF_BOUNDS_WEIGHT = 4;
const CARD_WEIGHT = 6;
const CARD_SPACING_PX = 12;
const PIN_WEIGHT = 6;
const ROUTE_WEIGHT = 0.4;

const SIDES: CardSide[] = ["above", "below", "right", "left", "above-start", "above-end", "below-start", "below-end"];

function sideBox(side: CardSide, [x, y]: readonly [number, number], gap: number, height: number): CardBox {
  const above = y - gap - height;
  const below = y + gap;
  if (side === "above") return { left: x - CARD_WIDTH_PX / 2, top: above };
  if (side === "below") return { left: x - CARD_WIDTH_PX / 2, top: below };
  if (side === "right") return { left: x + gap, top: y - height / 2 };
  if (side === "left") return { left: x - gap - CARD_WIDTH_PX, top: y - height / 2 };
  const left = side.endsWith("start") ? x - CORNER_INSET_PX : x - CARD_WIDTH_PX + CORNER_INSET_PX;
  return { left, top: side.startsWith("above") ? above : below };
}

function overlap(first: Rect, second: Rect): number {
  const width = Math.min(first.left + first.width, second.left + second.width) - Math.max(first.left, second.left);
  const height = Math.min(first.top + first.height, second.top + second.height) - Math.max(first.top, second.top);
  return width > 0 && height > 0 ? width * height : 0;
}

function outOfBounds(rect: Rect, size: PlacementSize): number {
  const left = Math.max(rect.left, -EDGE_SLACK_PX);
  const top = Math.max(rect.top, -EDGE_SLACK_PX);
  const keptWidth = Math.min(rect.left + rect.width, size.width + EDGE_SLACK_PX) - left;
  const keptHeight = Math.min(rect.top + rect.height, size.height + EDGE_SLACK_PX) - top;
  const kept = Math.max(keptWidth, 0) * Math.max(keptHeight, 0);
  return rect.width * rect.height - kept;
}

function cardObstacle(box: CardBox, height: number): Obstacle {
  return {
    left: box.left - CARD_SPACING_PX,
    top: box.top - CARD_SPACING_PX,
    width: CARD_WIDTH_PX + CARD_SPACING_PX * 2,
    height: height + CARD_SPACING_PX * 2,
    weight: CARD_WEIGHT,
  };
}

function pointObstacle([x, y]: readonly [number, number], clearance: number, weight: number): Obstacle {
  return { left: x - clearance, top: y - clearance, width: clearance * 2, height: clearance * 2, weight };
}

function cost(rect: Rect, obstacles: Obstacle[], size: PlacementSize): number {
  const collisions = obstacles.reduce((total, obstacle) => total + overlap(rect, obstacle) * obstacle.weight, 0);
  return collisions + outOfBounds(rect, size) * OUT_OF_BOUNDS_WEIGHT;
}

export function placeCards(
  requests: CardRequest[],
  pins: MapPoint[],
  routes: MapPoint[],
  size: PlacementSize,
  kept: Map<string, CardBox>,
): Map<string, CardBox> {
  const scale = size.width / US_MAP_WIDTH;
  const toPixels = (point: MapPoint): [number, number] => [point[0] * scale, point[1] * scale];
  const placed = new Map<string, CardBox>();
  const obstacles: Obstacle[] = [
    ...pins.map((pin) => pointObstacle(toPixels(pin), PIN_CLEARANCE_PX, PIN_WEIGHT)),
    ...routes.map((point) => pointObstacle(toPixels(point), ROUTE_CLEARANCE_PX, ROUTE_WEIGHT)),
  ];
  for (const request of requests) {
    const box = kept.get(request.key);
    if (box) {
      placed.set(request.key, box);
      obstacles.push(cardObstacle(box, request.height));
    }
  }
  for (const request of requests.filter((candidate) => !kept.has(candidate.key))) {
    const anchor = toPixels(request.point);
    const candidates = [request.preferred, ...SIDES.filter((side) => side !== request.preferred)];
    const scored = GAPS_PX.flatMap((gap) => candidates.map((side) => ({ side, gap }))).map(({ side, gap }) => {
      const box = sideBox(side, anchor, gap, request.height);
      const rect: Rect = { ...box, width: CARD_WIDTH_PX, height: request.height };
      return { box, rect, score: cost(rect, obstacles, size) };
    });
    const best = scored.reduce((chosen, candidate) => (candidate.score < chosen.score ? candidate : chosen));
    placed.set(request.key, best.box);
    obstacles.push(cardObstacle(best.box, request.height));
  }
  return placed;
}
